import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Switch,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
} from '@mui/material'
import {
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Storage as StorageIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { formatDate, formatNumber } from '../../utils/formatters'

const ProviderCard = ({
  provider,
  onToggle,
  onSync,
  loading = false,
  statusColor = 'default',
  statusText = 'Bilinmiyor'
}) => {
  
  const getProviderIcon = (providerName) => {
    // Provider specific icons or logos could be added here
    return <StorageIcon sx={{ fontSize: 40 }} color="primary" />
  }

  const getStatusIcon = () => {
    switch (statusColor) {
      case 'success':
        return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
      case 'warning':
        return <WarningIcon color="warning" sx={{ fontSize: 20 }} />
      case 'error':
        return <ErrorIcon color="error" sx={{ fontSize: 20 }} />
      default:
        return <InfoIcon color="info" sx={{ fontSize: 20 }} />
    }
  }

  const getPriceAvailability = () => {
    const priceCount = parseInt(provider.active_offerings || provider.price_count || 0)
    if (priceCount === 0) return { color: 'error', text: 'Fiyat Yok' }
    if (priceCount < 100) return { color: 'warning', text: 'Düşük' }
    if (priceCount < 500) return { color: 'info', text: 'Orta' }
    return { color: 'success', text: 'Yüksek' }
  }

  const priceAvailability = getPriceAvailability()

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
    >
      {loading && <LinearProgress />}
      
      <CardContent sx={{ flex: 1 }}>
        {/* Provider Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {getProviderIcon(provider.name)}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {provider.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {provider.api_name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon()}
            <Chip 
              label={statusText} 
              size="small" 
              color={statusColor}
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Provider Stats */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                {formatNumber(provider.active_offerings || provider.price_count || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fiyat
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                {formatNumber(provider.rental_count || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Kiralama
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Fiyat Durumu:
            </Typography>
            <Chip 
              label={priceAvailability.text} 
              size="small" 
              color={priceAvailability.color}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon sx={{ fontSize: 16 }} color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {provider.api_base_url}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Provider Details */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Oluşturulma:
            </Typography>
            <Typography variant="body2">
              {formatDate(provider.created_at, 'dd/MM/yyyy')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Son Güncelleme:
            </Typography>
            <Typography variant="body2">
              {formatDate(provider.updated_at, 'dd/MM/yyyy')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Durum:
            </Typography>
            <Switch
              checked={provider.is_active}
              onChange={onToggle}
              disabled={loading}
              size="small"
              color="primary"
            />
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Fiyatları Senkronize Et">
            <IconButton
              size="small"
              onClick={onSync}
              disabled={loading || !provider.is_active}
              color="primary"
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Provider Ayarları">
            <IconButton
              size="small"
              disabled={loading}
              color="default"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<InfoIcon />}
          disabled={loading}
          sx={{ ml: 'auto' }}
        >
          Detaylar
        </Button>
      </CardActions>
    </Card>
  )
}

export default ProviderCard